// "The client accepted this option": the server re-prices it from CRM and
// replaces the opportunity's product lines with it (PRICING.md "Accepting an
// option"). Offered only once the REVIEW allows the price and every change has
// synced, so the server prices exactly what's on screen; always confirmed
// first, and again before replacing lines someone typed by hand — in the
// app's centred question (#443).
import { useRef, useState, type ReactNode } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button, ConfirmDialog } from '@higgins/ui';
import { addToRegister } from '../lib/asset-register';
import { acceptOptionApi, ApiError, isConnectionFailure, type AcceptedFromApi } from '../lib/api';
import { formatMoney } from './surface-format';
import { ErrorMessage } from './message';

type Step =
  | { kind: 'idle' }
  | { kind: 'confirm' }
  | { kind: 'sending'; replacing: boolean; left?: number }
  | { kind: 'manual'; count: number; total: number }
  | { kind: 'done'; result: AcceptedFromApi }
  | { kind: 'error'; message: string };

interface Props {
  optionId: string;
  optionName: string;
  /** Already the accepted option (and since when). */
  acceptedOn: string | null;
  /** Why it can't be accepted right now; null = it can. */
  blockedBecause: string | null;
  sell: number;
  /** What accepting will write, shown before the button (a maintenance contract's year lines, #515). */
  preview?: ReactNode;
  /** Re-reads the job from CRM: after an Accept, and after a failed one, which may have landed all the same. */
  onAccepted: () => void;
}

/** Said after a failure that may have come part-way through writing: CRM marks the job's lines unfinished until then (#545). */
const UNFINISHED = "If it had started writing the lines, the job's CRM lines are unfinished: accept this option again to finish them.";
/** Statuses the server refuses with before writing anything (MANUAL_LINES, ACCEPT_IN_PROGRESS, WORK_ORDER_IN_APPROVAL, REVIEW_INCOMPLETE …), shown in its words. */
const isRefusal = (err: unknown) => err instanceof ApiError && err.status >= 400 && err.status < 500;

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return `Accepting needs signal. ${UNFINISHED}`;
  if (isRefusal(err)) return (err as ApiError).message;
  return `${err instanceof Error && err.message ? err.message : 'The option could not be accepted.'} ${UNFINISHED}`;
}

export function AcceptPanel({ optionId, optionName, acceptedOn, blockedBecause, sell, preview, onAccepted }: Props) {
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const button = useRef<HTMLButtonElement>(null);
  const cancel = (open: boolean) => !open && setStep({ kind: 'idle' });

  async function send(replaceManualLines: boolean) {
    setStep({ kind: 'sending', replacing: replaceManualLines });
    try {
      const result = await acceptOptionApi(optionId, replaceManualLines, (left) => setStep({ kind: 'sending', replacing: replaceManualLines, left }));
      setStep({ kind: 'done', result });
      onAccepted();
      // The job is won: its buildings and areas go into Field Service's register, apart from the Accept (lib/asset-register.ts).
      void addToRegister(optionId).catch((err) => console.warn('The job was accepted, but its register entries were not all made', err));
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { count?: number; total?: number } | undefined) : undefined;
      if (err instanceof ApiError && err.code === 'MANUAL_LINES') {
        setStep({ kind: 'manual', count: body?.count ?? 0, total: body?.total ?? 0 });
        return;
      }
      setStep({ kind: 'error', message: describe(err) });
      // A failure seen here (a lost answer, the gateway giving up) can still have written the lines and
      // accepted the option on the server: show CRM's state, not the device's (#515, 23 Oct 2026).
      onAccepted();
    }
  }

  return (
    <div className="mt-3 border-t border-slate-200 pt-3">
      <h4 className="mb-1 font-medium text-slate-900">When the client accepts this option</h4>
      {acceptedOn && step.kind !== 'done' && (
        <p className="mb-2 flex items-center gap-1 text-green-800">
          <CheckCircle2 className="h-4 w-4" /> Accepted on {acceptedOn}.
        </p>
      )}
      {preview}
      {(step.kind === 'idle' || step.kind === 'confirm' || step.kind === 'manual' || step.kind === 'error') && (
        <>
          <Button ref={button} size="sm" onClick={() => setStep({ kind: 'confirm' })} disabled={!!blockedBecause}>
            {acceptedOn ? 'Accept again (re-write the lines)' : 'Accept this option'}
          </Button>
          {blockedBecause && <p className="mt-1 text-xs text-slate-500">{blockedBecause}</p>}
        </>
      )}
      {step.kind === 'sending' && (
        <p className="text-slate-500">
          Re-pricing on the server and writing the lines…{step.left ? ` ${step.left} line change${step.left === 1 ? '' : 's'} still to write.` : ''}
        </p>
      )}
      <ConfirmDialog
        open={step.kind === 'confirm' || (step.kind === 'sending' && !step.replacing)}
        onOpenChange={cancel}
        title="Accept this option?"
        description={
          <>
            Write <strong>{optionName}</strong> ({formatMoney(sell)}) onto the opportunity as its product lines? This replaces the lines it has now, and un-accepts any other option.
          </>
        }
        confirmLabel="Accept"
        busyLabel="Accepting…"
        busy={step.kind === 'sending'}
        onConfirm={() => void send(false)}
        returnFocus={button}
      />
      <ConfirmDialog
        open={step.kind === 'manual' || (step.kind === 'sending' && step.replacing)}
        onOpenChange={cancel}
        title="Replace hand-typed lines?"
        description={
          step.kind === 'manual'
            ? `The opportunity has ${step.count} line(s) typed by hand, worth ${formatMoney(step.total)}. Replace them with ${optionName}?`
            : `Replace the hand-typed lines with ${optionName}?`
        }
        confirmLabel="Replace"
        busyLabel="Replacing…"
        destructive
        busy={step.kind === 'sending'}
        onConfirm={() => void send(true)}
        returnFocus={button}
      />
      {step.kind === 'done' && (
        <p className="rounded-md bg-green-50 p-2 text-green-900">
          Accepted. {step.result.lines.length} lines written ({step.result.lines.map((l) => l.productNumber).join(', ')}), {formatMoney(step.result.totals.sell)} sell, {formatMoney(step.result.totals.cost)} cost
          {step.result.replacedManualLines ? `; ${step.result.replacedManualLines} hand-typed line(s) replaced` : ''}. CRM now shows {formatMoney(step.result.crmSell)} sell
          {step.result.crmCost !== null ? ` and ${formatMoney(step.result.crmCost)} total cost` : ''}.
        </p>
      )}
      {step.kind === 'error' && <ErrorMessage className="mt-2">{step.message}</ErrorMessage>}
    </div>
  );
}
